import { Alert, Box, Divider } from "@mui/material";
import React from "react";
import LogoLoader from "../common/LogoLoader";
import { EdgeDetail } from "../../services/graphService";
import EdgeDetailComponent from "./EdgeDetailOverlay/EdgeDetail";
import EdgeHeader from "./EdgeDetailOverlay/EdgeHeader";
import EdgeRelationshipTabs from "./EdgeDetailOverlay/EdgeRelationshipTabs";

interface EdgeDetailContentProps {
  edgeDetailResponse: EdgeDetail | null;
  loading: boolean;
  error: string | null;
  onEntityClick?: (entityId: string) => void;
  onClose?: () => void;
  mode?: "overlay" | "standalone";
}

/**
 * Presentation component for relationship/edge details
 * Renders edge header, details, and source tabs
 * Can be used in both overlay and standalone contexts
 */
const EdgeDetailContent: React.FC<EdgeDetailContentProps> = ({
  edgeDetailResponse,
  loading,
  error,
  onEntityClick,
  onClose,
  mode = "overlay",
}) => {
  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: mode === "standalone" ? "400px" : "100%",
        }}
      >
        <LogoLoader size="panel" surface="paper" label="Loading the relation" />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  // No data state
  if (!edgeDetailResponse) {
    return null;
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Edge Header contains both functional and content headers */}
      {/* We need to wrap it to constrain the content header width in standalone mode */}
      <EdgeHeader
        edgeDetail={edgeDetailResponse}
        onClose={onClose || (() => {})}
        mode={mode}
      />

      {/* Centered Content Container (only for standalone mode) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: mode === "standalone" ? "center" : "stretch",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: mode === "standalone" ? "1200px" : "100%",
          }}
        >
          {/* Section 1: Edge Overview - Two-column entity display */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <EdgeDetailComponent
              edgeDetail={edgeDetailResponse}
              onEntityClick={onEntityClick}
            />
          </Box>

          <Divider />

          {/* Section 2: Relationship Sources Tab */}
          <EdgeRelationshipTabs
            edgeDetail={edgeDetailResponse}
            onEntityClick={onEntityClick}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EdgeDetailContent;
